import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useFinancialYear } from '../context/FinancialYearContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  CheckCircle2, 
  Wallet,
  Plus,
  ArrowRight,
  Banknote,
  AlertCircle,
  PieChart as PieChartIcon,
  BarChart3,
  XCircle,
  FileText
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const { selectedFY } = useFinancialYear();
  const [stats, setStats] = useState(null);
  const [pnlSummary, setPnlSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
    fetchPnlSummary();
  }, [selectedFY]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dashboard/stats?financial_year=${selectedFY}`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPnlSummary = async () => {
    try {
      const response = await axios.get(`${API}/pnl/ytd/${selectedFY}`, {
        headers: getAuthHeaders()
      });
      setPnlSummary(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch P&L summary:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { class: 'badge-pending', label: 'Pending' },
      APPROVED: { class: 'badge-approved', label: 'Approved' },
      PAID: { class: 'badge-paid', label: 'Paid' },
      REJECTED: { class: 'badge-rejected', label: 'Rejected' },
      ON_HOLD: { class: 'badge-on-hold', label: 'On Hold' }
    };
    const config = statusConfig[status] || statusConfig.PENDING;
    return <Badge className={`${config.class} font-mono text-xs`}>{config.label}</Badge>;
  };

  const COLORS = ['#1e1b4b', '#10b981', '#ef4444', '#f59e0b', '#6366f1'];

  const pieData = stats ? [
    { name: 'Pending', value: stats.pending_count, color: '#f59e0b' },
    { name: 'Approved', value: stats.approved_count, color: '#3b82f6' },
    { name: 'Paid', value: stats.paid_count, color: '#10b981' }
  ].filter(d => d.value > 0) : [];

  const typeData = stats?.expense_by_type ? Object.entries(stats.expense_by_type).map(([key, value]) => ({
    name: key.replace('_', ' ').replace('CORPORATE ', ''),
    amount: value
  })) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-950"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading text-indigo-950">Dashboard - FY {selectedFY}</h1>
          <p className="text-slate-500 text-sm mt-1">Overview of your expense tracking for the selected financial year</p>
        </div>
        <Button 
          className="bg-indigo-950 hover:bg-indigo-900"
          onClick={() => navigate('/expenses/new')}
          data-testid="add-expense-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Expense
        </Button>
      </div>

      {/* KPI Cards - Row 1: Key Payment Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="kpi-card animate-fade-in stagger-1 border-2 border-red-200 bg-gradient-to-br from-red-50 to-white cursor-pointer hover:shadow-md transition-shadow" data-testid="kpi-to-be-paid"
          onClick={() => navigate('/expenses?status=PENDING,APPROVED,PARTIALLY_PAID')}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="kpi-label text-red-700">Amount to be Paid</p>
                <p className="text-2xl font-bold font-mono text-red-600 mt-1">{formatCurrency(stats?.amount_to_be_paid)}</p>
                <p className="text-xs text-red-400 mt-1">Click to view</p>
              </div>
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card animate-fade-in stagger-2 border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white cursor-pointer hover:shadow-md transition-shadow" data-testid="kpi-total-paid"
          onClick={() => navigate('/expenses?status=PAID,PARTIALLY_PAID')}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="kpi-label text-emerald-700">Total Paid Amount</p>
                <p className="text-2xl font-bold font-mono text-emerald-600 mt-1">{formatCurrency(stats?.total_paid_amount)}</p>
                <p className="text-xs text-emerald-400 mt-1">Click to view</p>
              </div>
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card animate-fade-in stagger-3 border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white cursor-pointer hover:shadow-md transition-shadow" data-testid="kpi-invoices-raised"
          onClick={() => navigate('/invoices')}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="kpi-label text-blue-700">Invoices Raised</p>
                <p className="text-2xl font-bold font-mono text-blue-600 mt-1">{formatCurrency(stats?.invoices_raised)}</p>
                <p className="text-xs text-blue-400 mt-1">{stats?.invoices_raised_count || 0} invoices</p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Banknote className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card animate-fade-in stagger-4 border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white cursor-pointer hover:shadow-md transition-shadow" data-testid="kpi-invoices-received"
          onClick={() => navigate('/invoices')}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="kpi-label text-purple-700">Payment Received</p>
                <p className="text-2xl font-bold font-mono text-purple-600 mt-1">{formatCurrency(stats?.invoices_received)}</p>
                <p className="text-xs text-purple-400 mt-1">{stats?.invoices_received_count || 0} paid invoices</p>
              </div>
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Wallet className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card animate-fade-in stagger-4 border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white cursor-pointer hover:shadow-md transition-shadow" data-testid="kpi-estimates-pipeline"
          onClick={() => navigate('/estimates')}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="kpi-label text-indigo-700">Estimates Pipeline</p>
                <p className="text-2xl font-bold font-mono text-indigo-600 mt-1">{formatCurrency(stats?.estimates_pipeline)}</p>
                <p className="text-xs text-indigo-400 mt-1">{stats?.estimates_count || 0} estimates</p>
              </div>
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI Cards - Row 2: Status Breakdown - All clickable */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="kpi-card animate-fade-in stagger-1 cursor-pointer hover:shadow-md transition-shadow" data-testid="kpi-pending"
          onClick={() => navigate('/expenses?status=PENDING')}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-slate-500">Pending</p>
                <p className="text-xl font-bold font-mono text-slate-900 mt-1">{stats?.pending_count || 0}</p>
              </div>
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 font-mono mt-2">{formatCurrency(stats?.pending_amount)}</p>
          </CardContent>
        </Card>

        <Card className="kpi-card animate-fade-in stagger-2 cursor-pointer hover:shadow-md transition-shadow" data-testid="kpi-approved"
          onClick={() => navigate('/expenses?status=APPROVED')}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-slate-500">Approved</p>
                <p className="text-xl font-bold font-mono text-slate-900 mt-1">{stats?.approved_count || 0}</p>
              </div>
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-4 h-4 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 font-mono mt-2">{formatCurrency(stats?.approved_amount)}</p>
          </CardContent>
        </Card>

        <Card className="kpi-card animate-fade-in stagger-3 cursor-pointer hover:shadow-md transition-shadow" data-testid="kpi-partial"
          onClick={() => navigate('/expenses?status=PARTIALLY_PAID')}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-slate-500">Partially Paid</p>
                <p className="text-xl font-bold font-mono text-slate-900 mt-1">{stats?.partially_paid_count || 0}</p>
              </div>
              <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                <TrendingDown className="w-4 h-4 text-violet-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 font-mono mt-2">{formatCurrency(stats?.partially_paid_amount)}</p>
          </CardContent>
        </Card>

        <Card className="kpi-card animate-fade-in stagger-4 cursor-pointer hover:shadow-md transition-shadow" data-testid="kpi-paid"
          onClick={() => navigate('/expenses?status=PAID')}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-slate-500">Fully Paid</p>
                <p className="text-xl font-bold font-mono text-slate-900 mt-1">{stats?.paid_count || 0}</p>
              </div>
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 font-mono mt-2">{formatCurrency(stats?.paid_amount)}</p>
          </CardContent>
        </Card>

        {stats?.rejected_count > 0 && (
          <Card className="kpi-card animate-fade-in stagger-5 cursor-pointer hover:shadow-md transition-shadow border-red-200 bg-red-50"
            onClick={() => navigate('/expenses?status=REJECTED')} data-testid="kpi-rejected">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-red-600">Rejected</p>
                  <p className="text-xl font-bold font-mono text-red-700 mt-1">{stats?.rejected_count || 0}</p>
                </div>
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                  <XCircle className="w-4 h-4 text-red-600" />
                </div>
              </div>
              <p className="text-xs text-red-500 font-mono mt-2">{formatCurrency(stats?.rejected_amount)}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* P&L Summary Card */}
      {pnlSummary && pnlSummary.quarters_reported > 0 && (
        <Card className="border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 via-purple-50 to-blue-50 animate-fade-in">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <PieChartIcon className="w-5 h-5 text-indigo-600" />
                <h3 className="font-semibold text-indigo-950">P&L Summary (YTD)</h3>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/pnl')}
                className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
              >
                View Details
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/80 rounded-lg p-3 border border-slate-200">
                <p className="text-xs text-emerald-600 font-medium">Revenue</p>
                <p className="text-lg font-bold font-mono text-emerald-700">{formatCurrency(pnlSummary.revenue)}</p>
              </div>
              <div className="bg-white/80 rounded-lg p-3 border border-slate-200">
                <p className="text-xs text-blue-600 font-medium">Gross Profit</p>
                <div className="flex items-center gap-2">
                  <p className="text-lg font-bold font-mono text-blue-700">{formatCurrency(pnlSummary.gross_profit)}</p>
                  {pnlSummary.revenue > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                      {((pnlSummary.gross_profit / pnlSummary.revenue) * 100).toFixed(1)}%
                    </span>
                  )}
                </div>
              </div>
              <div className="bg-white/80 rounded-lg p-3 border border-slate-200">
                <p className="text-xs text-purple-600 font-medium">Net Profit</p>
                <p className="text-lg font-bold font-mono text-purple-700">{formatCurrency(pnlSummary.net_profit)}</p>
              </div>
              <div className="bg-white/80 rounded-lg p-3 border border-slate-200">
                <p className="text-xs text-indigo-600 font-medium">Your Share (50%)</p>
                <p className="text-lg font-bold font-mono text-indigo-700">{formatCurrency(pnlSummary.your_share || pnlSummary.net_profit * 0.5)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Expense Trend Chart */}
        <Card className="lg:col-span-2 border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading text-indigo-950">Expense Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.expense_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    axisLine={{ stroke: '#e2e8f0' }}
                    tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Amount']}
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                  />
                  <Bar dataKey="amount" fill="#1e1b4b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading text-indigo-950">Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [value, name]}
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-slate-400 text-sm">No data available</p>
              )}
            </div>
            {pieData.length > 0 && (
              <div className="flex justify-center gap-4 mt-2">
                {pieData.map((entry, index) => (
                  <div key={entry.name || `pie-${index}`} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                    <span className="text-xs text-slate-600">{entry.name}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Expenses */}
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg font-heading text-indigo-950">Recent Expenses</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/expenses')} data-testid="view-all-expenses-btn">
            View All
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Date</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Type</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Project / Description</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Amount</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody>
                {stats?.recent_expenses?.length > 0 ? (
                  stats.recent_expenses.slice(0, 5).map((expense, index) => (
                    <tr 
                      key={expense.id} 
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/expenses/${expense.id}`)}
                      data-testid={`expense-row-${index}`}
                    >
                      <td className="py-3 px-4 font-mono text-sm text-slate-600">
                        {expense.reporting_date || expense.created_at?.split('T')[0]}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-700">
                        {expense.expense_type?.replace('_', ' ')}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-900">
                        {expense.training_name || expense.description || expense.corporate_project_id || '-'}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-sm font-medium text-indigo-950">
                        {formatCurrency(expense.total_expenses)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {getStatusBadge(expense.payment_info?.payment_status)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-slate-400">
                      No expenses found. Start by adding an expense.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
